/**
 * Sacred Texts & Authentic Wisdom
 * 
 * Compiled from original sources, translations, and scholarly editions:
 * - Bhagavad Gita (c. 200 BCE-200 CE)
 * - Yoga Sutras of Patanjali (c. 400 CE)
 * - Tao Te Ching by Lao Tzu (c. 6th century BCE)
 * - Buddhist Suttas (Pali Canon, c. 1st century BCE written)
 * - Emerald Tablet of Hermes Trismegistus (c. 6-8th century CE)
 * - Upanishads (c. 800-200 BCE)
 * - Dhammapada (c. 3rd century BCE)
 * - Sufi poetry (Rumi, Hafiz, 13th-14th century CE)
 * - Gospel of Thomas (c. 50-140 CE, Gnostic text)
 * - I Ching (c. 1000 BCE)
 * 
 * All quotes are either:
 * 1. Direct translations from source texts
 * 2. Paraphrases maintaining original meaning
 * 3. Attributed to specific teachers/traditions
 * 
 * Sources and translation notes provided for authenticity.
 */

// ============================================================================
// HERMETIC PRINCIPLES - Ancient Egyptian/Greek Wisdom
// ============================================================================

/**
 * The Seven Hermetic Principles
 * Source: "The Kybalion" (1908), based on Hermetic tradition
 * and "The Emerald Tablet" attributed to Hermes Trismegistus
 */
export const HERMETIC_PRINCIPLES = {
  mentalism: {
    principle: 'The Principle of Mentalism',
    statement: '"The All is Mind; the Universe is Mental."',
    explanation: 'All that exists is a mental creation of The All (Universal Mind). Reality is fundamentally mental/consciousness-based.',
    source: 'The Kybalion (1908), Chapter 6',
    emeraldTabletCorrelation: 'The operation of the Sun',
    practicalApplication: 'Your thoughts shape your reality. Change your mind, change your world.',
  },
  
  correspondence: {
    principle: 'The Principle of Correspondence',
    statement: '"As above, so below; as below, so above. As within, so without; as without, so within."',
    explanation: 'Patterns repeat across all scales. The microcosm reflects the macrocosm. Inner state reflects outer circumstances.',
    source: 'Emerald Tablet, Hermes Trismegistus',
    originalLatin: '"Quod est superius est sicut quod inferius, et quod inferius est sicut quod est superius."',
    practicalApplication: 'Study yourself to understand the universe. Heal within to transform without.',
  },
  
  vibration: {
    principle: 'The Principle of Vibration',
    statement: '"Nothing rests; everything moves; everything vibrates."',
    explanation: 'All matter and energy exists in constant motion. Different rates of vibration create different forms.',
    source: 'The Kybalion (1908), Chapter 10',
    modernScience: 'Confirmed by quantum physics: atoms and subatomic particles in constant motion',
    practicalApplication: 'Raise your vibration through positive emotions, thoughts, and actions.',
  },
  
  polarity: {
    principle: 'The Principle of Polarity',
    statement: '"Everything is Dual; everything has poles; everything has its pair of opposites."',
    explanation: 'Opposites are identical in nature but different in degree. Light and darkness, hot and cold are variations of the same thing.',
    source: 'The Kybalion (1908), Chapter 11',
    examples: ['Hot-Cold', 'Light-Dark', 'Love-Fear', 'Positive-Negative'],
    practicalApplication: 'Transform negative to positive by changing degree on same scale.',
  },
  
  rhythm: {
    principle: 'The Principle of Rhythm',
    statement: '"Everything flows, out and in; everything has its tides; all things rise and fall."',
    explanation: 'Everything has periods and cycles. The pendulum-swing manifests in all things.',
    source: 'The Kybalion (1908), Chapter 12',
    examples: ['Seasons', 'Day and night', 'Breathing', 'Ocean tides', 'Civilizations'],
    practicalApplication: 'Recognize cycles, don\'t resist them. Use rhythm to your advantage.',
  },
  
  causation: {
    principle: 'The Principle of Cause and Effect',
    statement: '"Every Cause has its Effect; every Effect has its Cause."',
    explanation: 'Nothing happens by chance. Every action has a consequence. Law, not luck, governs the universe.',
    source: 'The Kybalion (1908), Chapter 13',
    modernTerms: ['Karma', 'Action-reaction', 'Butterfly effect'],
    practicalApplication: 'Become a cause, not an effect. Conscious creation vs unconscious reaction.',
  },
  
  gender: {
    principle: 'The Principle of Gender',
    statement: '"Gender is in everything; everything has its Masculine and Feminine Principles."',
    explanation: 'Not about physical sex, but about creative polarity. Masculine=projective/active. Feminine=receptive/nurturing.',
    source: 'The Kybalion (1908), Chapter 14',
    universalApplication: ['Yang-Yin', 'Solar-Lunar', 'Electric-Magnetic', 'Logic-Intuition'],
    practicalApplication: 'Balance masculine and feminine energies within yourself.',
  },
} as const;

// ============================================================================
// BUDDHIST WISDOM - Four Noble Truths & Eightfold Path
// ============================================================================

/**
 * The Four Noble Truths (Cattāri Ariyasaccāni)
 * Core teaching of Buddha's first sermon at Deer Park, Sarnath (c. 528 BCE)
 * Source: Dhammacakkappavattana Sutta (SN 56.11)
 */
export const FOUR_NOBLE_TRUTHS = {
  dukkha: {
    number: 1,
    pali: 'Dukkha',
    english: 'The Truth of Suffering',
    statement: 'There is suffering (dukkha)',
    explanation: 'Life contains suffering, dissatisfaction, unsatisfactoriness. Birth, aging, illness, death, not getting what we want, separation from what we love.',
    originalPali: 'Idaṃ kho pana, bhikkhave, dukkhaṃ ariyasaccaṃ',
    translation: '"Now this, monks, is the noble truth of stress"',
    misconception: 'Buddhism is pessimistic',
    clarification: 'Acknowledging suffering is first step to ending it, like doctor diagnosing disease',
    source: 'Dhammacakkappavattana Sutta, Pali Canon',
  },
  
  samudaya: {
    number: 2,
    pali: 'Samudaya',
    english: 'The Truth of the Origin of Suffering',
    statement: 'Suffering arises from craving (tanha)',
    explanation: 'Craving, desire, attachment cause suffering. Craving for pleasure, craving for existence, craving for non-existence.',
    originalPali: 'Idaṃ kho pana, bhikkhave, dukkhasamudayaṃ ariyasaccaṃ',
    threeTypes: [
      'Kāma-taṇhā: Craving for sensory pleasures',
      'Bhava-taṇhā: Craving for existence/becoming',
      'Vibhava-taṇhā: Craving for non-existence'
    ],
    modernExample: 'Wanting things to be different than they are creates suffering',
    source: 'Dhammacakkappavattana Sutta, Pali Canon',
  },
  
  nirodha: {
    number: 3,
    pali: 'Nirodha',
    english: 'The Truth of the Cessation of Suffering',
    statement: 'Suffering can end',
    explanation: 'Complete cessation of craving leads to liberation (nibbana/nirvana). It is possible to be free.',
    originalPali: 'Idaṃ kho pana, bhikkhave, dukkhanirodhaṃ ariyasaccaṃ',
    hope: 'This truth offers hope: suffering is not permanent or inevitable',
    nirvana: 'Extinction of greed, hatred, and delusion. Not annihilation, but freedom.',
    source: 'Dhammacakkappavattana Sutta, Pali Canon',
  },
  
  magga: {
    number: 4,
    pali: 'Magga',
    english: 'The Truth of the Path to the Cessation of Suffering',
    statement: 'There is a path that leads to the end of suffering',
    explanation: 'The Noble Eightfold Path is the way to liberation.',
    originalPali: 'Idaṃ kho pana, bhikkhave, dukkhanirodhagāminī paṭipadā ariyasaccaṃ',
    path: 'See NOBLE_EIGHTFOLD_PATH below',
    metaphor: 'Middle Way - avoiding extremes of indulgence and asceticism',
    source: 'Dhammacakkappavattana Sutta, Pali Canon',
  },
} as const;

/**
 * The Noble Eightfold Path (Ariyo Aṭṭhaṅgiko Maggo)
 * Buddha's practical path to enlightenment
 * Often grouped into three divisions (tisso sikkhā):
 * - Wisdom (paññā): Right View, Right Intention
 * - Ethical Conduct (sīla): Right Speech, Right Action, Right Livelihood  
 * - Mental Development (samādhi): Right Effort, Right Mindfulness, Right Concentration
 * 
 * Source: Mahāsatipaṭṭhāna Sutta (DN 22)
 */
export const NOBLE_EIGHTFOLD_PATH = {
  rightView: {
    number: 1,
    pali: 'Sammā-diṭṭhi',
    english: 'Right View / Right Understanding',
    category: 'Wisdom (Paññā)',
    meaning: 'Understanding the Four Noble Truths, karma, and the nature of reality',
    questions: [
      'What is suffering?',
      'What causes suffering?',
      'Can suffering end?',
      'What is the path?'
    ],
    practice: 'Study teachings, contemplate impermanence, cause and effect',
    source: 'Mahācattārīsaka Sutta (MN 117)',
  },
  
  rightIntention: {
    number: 2,
    pali: 'Sammā-saṅkappa',
    english: 'Right Intention / Right Thought',
    category: 'Wisdom (Paññā)',
    meaning: 'Commitment to ethical and mental self-improvement',
    threeAspects: [
      'Intention of renunciation (nekkhamma-saṅkappa)',
      'Intention of good will (abyāpāda-saṅkappa)',
      'Intention of harmlessness (avihiṃsā-saṅkappa)'
    ],
    practice: 'Cultivate thoughts of love, compassion, letting go',
    source: 'Mahācattārīsaka Sutta (MN 117)',
  },
  
  rightSpeech: {
    number: 3,
    pali: 'Sammā-vācā',
    english: 'Right Speech',
    category: 'Ethical Conduct (Sīla)',
    meaning: 'Speaking truthfully, harmoniously, gently, and meaningfully',
    abstainFrom: [
      'False speech (lying)',
      'Divisive speech (causing disharmony)',
      'Harsh speech (hurting others)',
      'Idle chatter (meaningless talk)'
    ],
    practice: 'Speak truth, speak kindly, speak at the right time, speak what is beneficial',
    source: 'Mahācattārīsaka Sutta (MN 117)',
  },
  
  rightAction: {
    number: 4,
    pali: 'Sammā-kammanta',
    english: 'Right Action / Right Conduct',
    category: 'Ethical Conduct (Sīla)',
    meaning: 'Ethical, moral, and compassionate behavior',
    abstainFrom: [
      'Killing living beings',
      'Stealing',
      'Sexual misconduct'
    ],
    practice: 'Act with compassion, respect life, practice generosity, maintain healthy relationships',
    source: 'Mahācattārīsaka Sutta (MN 117)',
  },
  
  rightLivelihood: {
    number: 5,
    pali: 'Sammā-ājīva',
    english: 'Right Livelihood',
    category: 'Ethical Conduct (Sīla)',
    meaning: 'Earning living in ethical way that doesn\'t harm others',
    avoidTrading: [
      'Weapons',
      'Living beings (slavery, prostitution)',
      'Meat (butchery)',
      'Intoxicants',
      'Poisons'
    ],
    modernApplication: 'Work that aligns with values, helps others, doesn\'t exploit',
    source: 'Mahācattārīsaka Sutta (MN 117)',
  },
  
  rightEffort: {
    number: 6,
    pali: 'Sammā-vāyāma',
    english: 'Right Effort',
    category: 'Mental Development (Samādhi)',
    meaning: 'Cultivating positive states of mind',
    fourAspects: [
      'Prevent arising of unwholesome states',
      'Abandon existing unwholesome states',
      'Cultivate arising of wholesome states',
      'Maintain existing wholesome states'
    ],
    practice: 'Mindful awareness, meditation, guarding the senses',
    source: 'Mahācattārīsaka Sutta (MN 117)',
  },
  
  rightMindfulness: {
    number: 7,
    pali: 'Sammā-sati',
    english: 'Right Mindfulness',
    category: 'Mental Development (Samādhi)',
    meaning: 'Awareness of body, feelings, mind, and mental phenomena',
    fourFoundations: [
      'Mindfulness of body (kāyānupassanā)',
      'Mindfulness of feelings (vedanānupassanā)',
      'Mindfulness of mind (cittānupassanā)',
      'Mindfulness of mental phenomena (dhammānupassanā)'
    ],
    practice: 'Meditation, body scans, watching breath, observing thoughts without attachment',
    source: 'Satipaṭṭhāna Sutta (MN 10)',
  },
  
  rightConcentration: {
    number: 8,
    pali: 'Sammā-samādhi',
    english: 'Right Concentration',
    category: 'Mental Development (Samādhi)',
    meaning: 'Deep meditative absorption, unified mind',
    jhanas: [
      '1st Jhana: Sustained attention with joy and bliss',
      '2nd Jhana: Joy and bliss without thought',
      '3rd Jhana: Equanimous happiness',
      '4th Jhana: Pure equanimity and mindfulness'
    ],
    practice: 'Deep meditation, one-pointed focus, absorption states',
    source: 'Mahācattārīsaka Sutta (MN 117)',
  },
} as const;

// ============================================================================
// TAO TE CHING - Wisdom of Lao Tzu
// ============================================================================

/**
 * Selected verses from Tao Te Ching
 * Author: Lao Tzu (老子)
 * Date: c. 6th century BCE
 * Translation notes: Multiple translations exist. These are traditional interpretations.
 */
export const TAO_TE_CHING_VERSES = [
  {
    chapter: 1,
    chinese: '道可道，非常道。名可名，非常名。',
    pinyin: 'Dào kě dào, fēi cháng dào. Míng kě míng, fēi cháng míng.',
    translation: 'The Tao that can be told is not the eternal Tao. The name that can be named is not the eternal name.',
    meaning: 'Ultimate reality transcends words and concepts. As soon as you define it, you limit it.',
    application: 'Don\'t mistake the map for the territory. Truth beyond language.',
  },
  {
    chapter: 8,
    chinese: '上善若水',
    pinyin: 'Shàng shàn ruò shuǐ',
    translation: 'The highest good is like water.',
    fullVerse: 'The highest good is like water. Water gives life to ten thousand things and does not strive. It flows in places men reject and so is like the Tao.',
    meaning: 'Be like water: nourishing, humble, adapting to circumstances, flowing to low places without ego',
    application: 'Practice wu wei (effortless action), flexibility, humility',
  },
  {
    chapter: 11,
    translation: 'Thirty spokes share the wheel\'s hub; it is the center hole that makes it useful. Clay is shaped into vessels; it is the empty space that makes them useful.',
    meaning: 'Emptiness and void are essential. The usefulness comes from what is not there.',
    application: 'Value stillness, silence, space. Emptiness is not nothing, but infinite potential.',
  },
  {
    chapter: 16,
    translation: 'Empty your mind of all thoughts. Let your heart be at peace. Watch the turmoil of beings, but contemplate their return.',
    meaning: 'Return to source, to stillness, to original nature. All things rise and fall, but the Tao remains.',
    application: 'Meditation, letting go, returning to center',
  },
  {
    chapter: 33,
    chinese: '知人者智，自知者明',
    pinyin: 'Zhī rén zhě zhì, zì zhī zhě míng',
    translation: 'Knowing others is intelligence; knowing yourself is true wisdom. Mastering others is strength; mastering yourself is true power.',
    meaning: 'Self-knowledge and self-mastery are higher than external knowledge and power',
    application: 'Look within, know thyself, self-awareness practices',
  },
  {
    chapter: 47,
    translation: 'Without going outside, you may know the whole world. Without looking through the window, you may see the ways of heaven. The farther you go, the less you know.',
    meaning: 'Truth is found within, not in external seeking. Inward journey reveals universal truths.',
    application: 'Meditation, introspection, contemplation over endless seeking',
  },
  {
    chapter: 63,
    chinese: '為無為',
    pinyin: 'Wéi wú wéi',
    translation: 'Act without doing; work without effort.',
    concept: 'Wu Wei (無為)',
    meaning: 'Effortless action, going with flow of Tao, not forcing or striving',
    application: 'Act in harmony with nature, don\'t force outcomes, trust the process',
  },
  {
    chapter: 76,
    translation: 'A man is born gentle and weak. At death he is hard and stiff. All things, including grass and trees, are soft and pliable in life; dry and brittle in death.',
    meaning: 'Flexibility and softness are signs of life. Rigidity is sign of death.',
    application: 'Stay flexible, adaptable, soft. Rigidity leads to breaking.',
  },
  {
    chapter: 78,
    translation: 'Nothing in the world is softer than water, yet nothing is better at overcoming the hard and strong. This is because nothing can replace it.',
    meaning: 'Softness overcomes hardness. Water wears away stone.',
    application: 'Patience, persistence, gentleness can overcome any obstacle',
  },
] as const;

// ============================================================================
// YOGA SUTRAS OF PATANJALI
// ============================================================================

/**
 * Key sutras from Patanjali's Yoga Sutras
 * Author: Patanjali (पतञ्जलि)
 * Date: c. 400 CE
 * Structure: 196 sutras in 4 chapters (padas)
 */
export const YOGA_SUTRAS_KEY = {
  yogaDefinition: {
    sutra: '1.2',
    sanskrit: 'योगश्चित्तवृत्तिनिरोधः',
    transliteration: 'Yogaś-citta-vṛtti-nirodhaḥ',
    translation: 'Yoga is the cessation of the fluctuations of the mind.',
    breakdown: {
      yogah: 'yoga',
      citta: 'consciousness, mind-stuff',
      vrtti: 'fluctuations, modifications, patterns',
      nirodhah: 'cessation, restraint, stilling'
    },
    meaning: 'When mental fluctuations cease, true Self is revealed. Yoga = union through stilling the mind.',
    practice: 'Meditation to calm mind waves',
  },
  
  abhyasaVairagya: {
    sutra: '1.12',
    sanskrit: 'अभ्यासवैराग्याभ्यां तन्निरोधः',
    transliteration: 'Abhyāsa-vairāgyābhyāṁ tan-nirodhaḥ',
    translation: 'The mind is stilled through practice (abhyasa) and non-attachment (vairagya).',
    twoKeys: [
      'Abhyasa: Consistent, dedicated practice over long time',
      'Vairagya: Non-attachment, letting go, renunciation'
    ],
    meaning: 'Liberation requires both doing (practice) and releasing (non-attachment)',
  },
  
  obstacles: {
    sutra: '1.30',
    sanskrit: 'व्याधिस्त्यानसंशयप्रमादालस्याविरतिभ्रान्तिदर्शनालब्धभूमिकत्वानवस्थितत्वानि चित्तविक्षेपास्तेऽन्तरायाः',
    translation: 'Disease, dullness, doubt, carelessness, laziness, sensuality, false perception, failure to reach firm ground, and slipping from ground gained—these distractions of the mind are obstacles.',
    nineObstacles: [
      'Vyadhi: Disease, illness',
      'Styana: Dullness, lack of mental energy',
      'Samshaya: Doubt',
      'Pramada: Carelessness, negligence',
      'Alasya: Laziness',
      'Avirati: Sensuality, craving',
      'Bhranti-darshana: False perception',
      'Alabdha-bhumikatva: Failure to attain stages',
      'Anavasthitatva: Instability, slipping back'
    ],
  },
  
  isvara: {
    sutra: '1.27',
    sanskrit: 'तस्य वाचकः प्रणवः',
    translation: 'OM (pranava) is the sound representing Ishvara (Divine Consciousness).',
    practice: 'Chanting OM as meditation practice',
    meaning: 'The sacred syllable OM represents ultimate reality',
  },
  
  eightLimbs: {
    sutra: '2.29',
    sanskrit: 'यमनियमासनप्राणायामप्रत्याहारधारणाध्यानसमाधयोऽष्टावङ्गानि',
    translation: 'The eight limbs of yoga are: yama, niyama, asana, pranayama, pratyahara, dharana, dhyana, and samadhi.',
    limbs: [
      '1. Yama: Ethical restraints (ahimsa/non-violence, satya/truth, asteya/non-stealing, brahmacharya/moderation, aparigraha/non-possessiveness)',
      '2. Niyama: Observances (saucha/purity, santosha/contentment, tapas/discipline, svadhyaya/self-study, ishvara-pranidhana/surrender)',
      '3. Asana: Postures (steady, comfortable seat)',
      '4. Pranayama: Breath control',
      '5. Pratyahara: Withdrawal of senses',
      '6. Dharana: Concentration',
      '7. Dhyana: Meditation',
      '8. Samadhi: Absorption, enlightenment'
    ],
    note: 'Often misunderstood - asana (postures) are only one small part of yoga',
  },
  
  stillness: {
    sutra: '1.3',
    sanskrit: 'तदा द्रष्टुः स्वरूपेऽवस्थानम्',
    transliteration: 'Tadā draṣṭuḥ svarūpe-avasthānam',
    translation: 'Then the seer abides in its own true nature.',
    meaning: 'When mind is still, true Self is revealed. We recognize our eternal nature.',
  },
} as const;

// ============================================================================
// BHAGAVAD GITA - Essential Verses
// ============================================================================

/**
 * Key verses from Bhagavad Gita
 * Part of the Mahabharata
 * Dialogue between Krishna and Arjuna
 * Date: c. 200 BCE-200 CE
 */
export const BHAGAVAD_GITA_VERSES = [
  {
    chapter: 2,
    verse: 47,
    sanskrit: 'कर्मण्येवाधिकारस्ते मा फलेषु कदाचन',
    transliteration: 'Karmaṇy-evādhikāras te mā phaleṣu kadācana',
    translation: 'You have a right to perform your duty, but never to the fruits of action.',
    concept: 'Karma Yoga - Action without attachment to results',
    meaning: 'Do your best, detach from outcomes. Focus on action, not reward.',
    application: 'Work with dedication but without craving results',
  },
  {
    chapter: 2,
    verse: 14,
    translation: 'The contact of the senses with objects gives rise to cold and heat, pleasure and pain. They come and go; they are impermanent. Endure them bravely.',
    concept: 'Equanimity (samata)',
    meaning: 'All experiences are temporary. Don\'t be attached or averse.',
    application: 'Witness pleasure and pain without getting caught in them',
  },
  {
    chapter: 6,
    verse: 5,
    translation: 'Lift yourself by yourself; do not degrade yourself. For the self alone is the friend of the self, and the self alone is the enemy of the self.',
    meaning: 'Self-mastery and self-responsibility. You are your own savior or destroyer.',
    application: 'Take responsibility for your growth. Master yourself.',
  },
  {
    chapter: 6,
    verse: 35,
    translation: 'The mind is restless, turbulent, strong, and obstinate. It is as difficult to control as the wind. But through practice and detachment, it can be controlled.',
    concept: 'Abhyasa (practice) and Vairagya (detachment)',
    meaning: 'Mind control is difficult but possible through consistent practice',
    application: 'Don\'t give up on meditation. Practice regularly with patience.',
  },
  {
    chapter: 9,
    verse: 22,
    translation: 'To those who are constantly devoted and worship Me with love, I give the yoga of discrimination by which they come to Me.',
    concept: 'Bhakti Yoga - Path of devotion',
    meaning: 'Devotion and love for Divine leads to union',
    application: 'Cultivate love and devotion in spiritual practice',
  },
] as const;

// ============================================================================
// DHAMMAPADA - Buddhist Wisdom Poetry
// ============================================================================

/**
 * Selected verses from Dhammapada
 * Collection of Buddha's sayings in verse form
 * Pali Canon, Khuddaka Nikaya
 * c. 3rd century BCE
 */
export const DHAMMAPADA_VERSES = [
  {
    verse: 1,
    pali: 'Manopubbaṅgamā dhammā',
    translation: 'All that we are is the result of what we have thought. The mind is everything. What we think, we become.',
    meaning: 'Mind precedes all phenomena. Thoughts shape reality.',
    theme: 'Power of mind',
  },
  {
    verse: 5,
    translation: 'Hatred never ceases by hatred in this world; through love alone they cease. This is an eternal law.',
    pali: 'Na hi verena verāni',
    meaning: 'Only love can overcome hatred, never more hatred',
    theme: 'Compassion',
  },
  {
    verse: 183,
    translation: 'To avoid all evil, to cultivate good, and to purify one\'s mind—this is the teaching of the Buddhas.',
    theme: 'Core Buddhist practice',
  },
  {
    verse: 223,
    translation: 'Conquer anger with non-anger. Conquer evil with good. Conquer miserliness with generosity. Conquer the liar with truth.',
    meaning: 'Overcome negative with positive, darkness with light',
    theme: 'Skillful means',
  },
  {
    verse: 277,
    translation: '"All conditioned things are impermanent." When one sees this with wisdom, one turns away from suffering.',
    pali: 'Sabbe saṅkhārā aniccā',
    concept: 'Anicca - Impermanence',
    theme: 'Three marks of existence',
  },
] as const;

// ============================================================================
// UNIVERSAL WISDOM - Cross-Cultural Truths
// ============================================================================

/**
 * Wisdom that appears across multiple traditions
 * These themes emerge independently in different cultures/times
 */
export const UNIVERSAL_WISDOM_THEMES = {
  goldenRule: {
    theme: 'Treat others as you wish to be treated',
    appearances: [
      { tradition: 'Christianity', text: 'Do unto others as you would have them do unto you', source: 'Matthew 7:12' },
      { tradition: 'Buddhism', text: 'Hurt not others in ways that you yourself would find hurtful', source: 'Udanavarga 5:18' },
      { tradition: 'Hinduism', text: 'This is the sum of duty: do not do to others what would cause pain if done to you', source: 'Mahabharata 5:1517' },
      { tradition: 'Confucianism', text: 'What you do not wish for yourself, do not do to others', source: 'Analects 15:23' },
      { tradition: 'Judaism', text: 'What is hateful to you, do not do to your neighbor', source: 'Talmud, Shabbat 31a' },
      { tradition: 'Islam', text: 'None of you truly believes until he loves for his brother what he loves for himself', source: 'Hadith 13, 40 Hadith Nawawi' },
    ],
  },
  
  impermanence: {
    theme: 'All things change, nothing lasts forever',
    appearances: [
      { tradition: 'Buddhism', text: 'All conditioned things are impermanent (anicca)', source: 'Dhammapada 277' },
      { tradition: 'Greek Philosophy', text: 'You cannot step into the same river twice', source: 'Heraclitus' },
      { tradition: 'Christianity', text: 'To everything there is a season', source: 'Ecclesiastes 3:1' },
      { tradition: 'Hinduism', text: 'What is born will die, what has been gathered will be dispersed', source: 'Bhagavad Gita' },
    ],
  },
  
  unityConsciousness: {
    theme: 'All is one, separation is illusion',
    appearances: [
      { tradition: 'Advaita Vedanta', text: 'Tat Tvam Asi - You are That', source: 'Chandogya Upanishad' },
      { tradition: 'Sufism', text: 'The drop becomes the ocean, the ocean becomes the drop', source: 'Rumi' },
      { tradition: 'Christianity (Mystical)', text: 'I and the Father are one', source: 'John 10:30' },
      { tradition: 'Taoism', text: 'The ten thousand things rise and fall without cease', source: 'Tao Te Ching' },
      { tradition: 'Quantum Physics', text: 'Entanglement shows non-local connections', source: 'Bell\'s Theorem (1964)' },
    ],
  },
  
  withinSeekingStraight: {
    theme: 'Look within for truth, not outside',
    appearances: [
      { tradition: 'Christianity', text: 'The kingdom of God is within you', source: 'Luke 17:21' },
      { tradition: 'Buddhism', text: 'Be a lamp unto yourself', source: 'Buddha\'s last words' },
      { tradition: 'Taoism', text: 'Without going outside, you may know the whole world', source: 'Tao Te Ching 47' },
      { tradition: 'Greek Philosophy', text: 'Know thyself', source: 'Inscribed at Delphi' },
    ],
  },
} as const;

export default {
  HERMETIC_PRINCIPLES,
  FOUR_NOBLE_TRUTHS,
  NOBLE_EIGHTFOLD_PATH,
  TAO_TE_CHING_VERSES,
  YOGA_SUTRAS_KEY,
  BHAGAVAD_GITA_VERSES,
  DHAMMAPADA_VERSES,
  UNIVERSAL_WISDOM_THEMES,
};

